/**
 * The personal inbox's filter state, as a URL.
 *
 * ## The defect this fixes
 *
 * `PersonalInboxSurface` kept `filters` and `folder` in `useState` and wrote
 * nothing to the address bar. Opening a message unmounts the surface, so
 * pressing Back remounted it with `EMPTY_FILTERS` — every filter, the source
 * pill and the folder, gone. The reported shape was "apply a filter, switch
 * the platform switcher, open a mail, press Back, and the filters are lost";
 * the same loss happened on a refresh, on a shared link, and on any route that
 * unmounts the list. The unified lane has never had this because its filters
 * have always lived in `location.search`.
 *
 * ## Why this is a SECOND codec and not a widened first one
 *
 * `InboxFiltersBar`'s `filtersFromSearch` / `searchFromFilters` speak the
 * unified list's vocabulary — `sort`, `mailbox` — and the personal list speaks
 * `sortBy`, `provider`, `unread`. They are the same ideas over different APIs,
 * and folding both into one function means a `sort` written by one lane being
 * read as a `sortBy` by the other. `mailSortOptions.js` already draws this line
 * for the sort menu; this is the same line for the whole filter set.
 *
 * Two rules are copied deliberately, because they are right in both places:
 * `page` is never written (how far someone has scrolled is not a bookmarkable
 * view, and a shared link that lands on page 7 with pages 1-6 missing is worse
 * than one that starts at the top), and defaults are never written, so the URL
 * of an untouched inbox is just `/inbox`.
 */

/** The filter shape the surface holds, with everything at its default. */
export const EMPTY_PERSONAL_FILTERS = {
    search: undefined,
    sender: undefined,
    dateFrom: undefined,
    dateTo: undefined,
    replied: undefined,
    unread: undefined,
    starred: undefined,
    hasAttachment: undefined,
    provider: undefined,
    // #646 — a tag_id from the rail's TAGS section. Composes with the folder.
    tag: undefined,
    direction: 'desc',
    sortBy: 'comm_date',
};

export const DEFAULT_FOLDER = 'inbox';

const bool = (raw) => (raw === null ? undefined : raw === 'true');

/** Decode `location.search` into `{ filters, folder }`. */
export function personalStateFromSearch(search) {
    const p = new URLSearchParams(search || '');
    const filters = { ...EMPTY_PERSONAL_FILTERS };

    if (p.get('search')) filters.search = p.get('search');
    if (p.get('sender')) filters.sender = p.get('sender');
    if (p.get('date_from')) filters.dateFrom = p.get('date_from');
    if (p.get('date_to')) filters.dateTo = p.get('date_to');
    if (p.has('replied')) filters.replied = bool(p.get('replied'));
    if (p.has('unread')) filters.unread = bool(p.get('unread'));
    if (p.has('starred')) filters.starred = bool(p.get('starred'));
    if (p.get('has_attachment') === 'true') filters.hasAttachment = true;
    // The source pills — the "platform switcher" in the report.
    if (p.get('provider')) filters.provider = p.get('provider');
    if (p.get('tag')) filters.tag = p.get('tag');
    if (p.get('sort_by')) filters.sortBy = p.get('sort_by');
    if (p.get('dir') === 'asc') filters.direction = 'asc';

    return { filters, folder: p.get('folder') || DEFAULT_FOLDER };
}

/** The inverse. Only non-defaults are written. */
export function searchFromPersonalState(filters = {}, folder = DEFAULT_FOLDER) {
    const p = new URLSearchParams();
    if (filters.search) p.set('search', filters.search);
    if (filters.sender) p.set('sender', filters.sender);
    if (filters.dateFrom) p.set('date_from', filters.dateFrom);
    if (filters.dateTo) p.set('date_to', filters.dateTo);
    if (filters.replied !== undefined) p.set('replied', String(filters.replied));
    if (filters.unread !== undefined) p.set('unread', String(filters.unread));
    if (filters.starred !== undefined) p.set('starred', String(filters.starred));
    if (filters.hasAttachment) p.set('has_attachment', 'true');
    if (filters.provider) p.set('provider', filters.provider);
    if (filters.tag) p.set('tag', filters.tag);
    if (filters.sortBy && filters.sortBy !== 'comm_date') p.set('sort_by', filters.sortBy);
    if (filters.direction === 'asc') p.set('dir', 'asc');
    if (folder && folder !== DEFAULT_FOLDER) p.set('folder', folder);
    const qs = p.toString();
    return qs ? `?${qs}` : '';
}

/**
 * Whether two states would produce the same URL.
 *
 * The surface writes with `replace: true` on every filter change, and writing
 * an identical entry on each render is how a `useEffect`-driven navigate turns
 * into a loop. Comparing the ENCODED form rather than the objects means a
 * filter set that differs only in `undefined` vs absent compares equal, which
 * is what the round-trip actually produces.
 */
export function samePersonalState(a, b) {
    return searchFromPersonalState(a.filters, a.folder)
        === searchFromPersonalState(b.filters, b.folder);
}
